import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { X, ChevronRight, ChevronLeft, Play } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Phase 3B Batch 2 — M-CRIT-1: module-level fallback dismissal flag.
// If a user's browser blocks/quotas localStorage (Safari private mode, content
// blockers, certain PWA shells, iOS WebView storage corruption), the tour was
// previously stuck re-appearing on every mount because localStorage.setItem
// silently threw and onComplete() never ran. This in-memory flag survives
// route changes within the same SPA session and ensures the tour stays closed
// even when persistent storage is unavailable. The full hardening: every
// localStorage call is wrapped, every dismissal path falls through to set
// this flag, and onComplete() is guaranteed to fire.
let TOUR_DISMISSED_THIS_SESSION = false;

const _safeReadDone = () => {
  try {
    return localStorage.getItem('stellar_onboarding_done');
  } catch (_e) {
    return null; // storage unavailable — treat as "not done", session flag catches it
  }
};
const _safeWriteDone = () => {
  try {
    localStorage.setItem('stellar_onboarding_done', 'true');
    return true;
  } catch (e) {
    // Diagnostics breadcrumb without crashing the dismissal flow.
    try { console.warn('[OnboardingTour] localStorage.setItem failed, falling back to session flag + backend:', e?.name || e); } catch (_) { /* noop */ }
    return false;
  }
};
const _safeClearDone = () => {
  try { localStorage.removeItem('stellar_onboarding_done'); } catch (_e) { /* noop */ }
};

// Phase 3B Batch 2 — M-CRIT-1: backend fallback for browsers where localStorage
// fails. Survives full browser reloads / tab restarts. Fire-and-forget so it
// doesn't delay the visual dismissal. Auth header optional — endpoint requires
// auth and will silently fail if user isn't logged in (which can't happen since
// the tour only shows post-login).
const _persistDismissalToBackend = () => {
  if (!API) return;
  try {
    const token = localStorage.getItem('stellar_token');
    if (!token) return;
    fetch(`${API}/api/users/me/tour-dismissed`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => { /* swallow network errors — session flag still holds */ });
  } catch (_) { /* localStorage read on `stellar_token` may itself throw; that's fine */ }
};

const TOUR_STEPS = [
  {
    target: '[data-testid="task-dashboard"]',
    fallback: 'main',
    title: 'Welcome to Stellar Task Manager',
    description: 'This is your command center. Every morning, you\'ll see a personalized briefing with your tasks, capacity, and smart suggestions.',
    position: 'center',
  },
  {
    target: '[data-testid="smart-notifications"]',
    fallback: '.space-y-2',
    title: 'Smart AI Notifications',
    description: 'These intelligent alerts analyze your workload in real-time. When your daily load drops below 2 hours, it suggests upcoming tasks to work on next.',
    position: 'bottom',
  },
  {
    target: '[data-tour="capacity-meter"]',
    fallback: '.col-span-2',
    title: 'Capacity Meter',
    description: 'Your daily capacity is 8 hours. As you move tasks to "In Review" or "Completed", hours are instantly subtracted from your active load. This keeps you balanced.',
    position: 'bottom',
  },
  {
    target: '[data-testid="view-priority"]',
    fallback: null,
    title: 'Dual View: Priority & Board',
    description: 'Switch between Priority List (grouped by urgency) and Kanban Board (drag tasks between status columns). Both update your workload in real-time.',
    position: 'bottom',
  },
  {
    target: '[data-testid="nav-tasks"]',
    fallback: null,
    title: 'Tasks',
    description: 'View all tasks in List or Board mode. Use tabs to filter by My Tasks, Team, All, By Client, or By Status. Create tasks with rich descriptions, set recurrence (daily/weekly/monthly), and assign to team members.',
    position: 'right',
  },
  {
    target: '[data-testid="sidebar-create-page"]',
    fallback: null,
    title: 'Notion-Style Pages',
    description: 'Create pages with a rich block editor. Type "/" for slash commands: headings, lists, to-do checkboxes, tables, images, and more. Choose from 8 templates.',
    position: 'right',
  },
];

export function OnboardingTour({ onComplete }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(true);
  const [tooltipPos, setTooltipPos] = useState({ top: '50%', left: '50%' });

  const positionTooltip = useCallback(() => {
    const s = TOUR_STEPS[step];
    const el = document.querySelector(s.target) || (s.fallback ? document.querySelector(s.fallback) : null);

    if (el) {
      const rect = el.getBoundingClientRect();
      el.classList.add('onboarding-highlight');

      let top, left;
      if (s.position === 'bottom') {
        top = rect.bottom + 16;
        left = Math.max(20, rect.left);
      } else if (s.position === 'right') {
        top = rect.top;
        left = rect.right + 16;
      } else {
        top = window.innerHeight / 2 - 100;
        left = window.innerWidth / 2 - 190;
      }

      // Keep in viewport
      top = Math.min(top, window.innerHeight - 250);
      left = Math.min(left, window.innerWidth - 400);

      setTooltipPos({ top: `${top}px`, left: `${left}px` });
    } else {
      setTooltipPos({ top: '50%', left: '50%' });
    }

    return () => {
      if (el) el.classList.remove('onboarding-highlight');
    };
  }, [step]);

  useEffect(() => {
    const cleanup = positionTooltip();
    return cleanup;
  }, [positionTooltip]);

  const next = () => {
    // Remove highlight from current
    const s = TOUR_STEPS[step];
    const el = document.querySelector(s.target) || (s.fallback ? document.querySelector(s.fallback) : null);
    if (el) el.classList.remove('onboarding-highlight');

    if (step < TOUR_STEPS.length - 1) {
      setStep(step + 1);
    } else {
      finish();
    }
  };

  const prev = () => {
    const s = TOUR_STEPS[step];
    const el = document.querySelector(s.target) || (s.fallback ? document.querySelector(s.fallback) : null);
    if (el) el.classList.remove('onboarding-highlight');
    if (step > 0) setStep(step - 1);
  };

  const finish = () => {
    // Phase 3B Batch 2 — M-CRIT-1: hardened dismissal.
    // 1. Set in-memory session flag FIRST (guaranteed, can't throw).
    // 2. Attempt persistent write (best-effort, swallowed on failure).
    // 3. Always update local visibility + parent's showTour state, regardless
    //    of whether localStorage succeeded. This guarantees X / Skip / Finish
    //    actually dismiss the tour even on browsers that block storage.
    TOUR_DISMISSED_THIS_SESSION = true;
    _safeWriteDone();
    _persistDismissalToBackend();
    TOUR_STEPS.forEach(s => {
      try {
        const el = document.querySelector(s.target) || (s.fallback ? document.querySelector(s.fallback) : null);
        if (el) el.classList.remove('onboarding-highlight');
      } catch (_) { /* swallow any DOM exception so onComplete still fires */ }
    });
    setVisible(false);
    if (onComplete) {
      try { onComplete(); } catch (e) { try { console.warn('[OnboardingTour] onComplete threw:', e); } catch (_) {} }
    }
  };

  if (!visible) return null;

  const current = TOUR_STEPS[step];

  return (
    <>
      {/*
        Phase 2 Group C2 — overlay must NEVER intercept navigation clicks.
        Previously this div had onClick={finish} + default pointer-events,
        which meant clicking "Automations"/"Settings" in the sidebar landed
        on the overlay (z:9998), dismissed the tour, and dropped the navigation.
        Now: pointer-events:none lets clicks pass through to the underlying
        sidebar / page elements. The tour is closed only via the X button
        or the "Skip tour" / "Finish" buttons in the tooltip.
      */}
      <div className="onboarding-overlay" style={{ pointerEvents: 'none' }} aria-hidden="true" />
      <div className="onboarding-tooltip" style={tooltipPos}>
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-1">
            {TOUR_STEPS.map((_, i) => (
              <div key={i} className={`h-1 w-6 rounded-full transition-colors ${i === step ? 'bg-primary' : i < step ? 'bg-primary/40' : 'bg-border'}`} />
            ))}
          </div>
          <button onClick={finish} className="text-muted-foreground hover:text-foreground p-1 -mr-1">
            <X className="h-4 w-4" />
          </button>
        </div>

        <h3 className="text-base font-semibold mb-2" style={{ fontFamily: 'var(--font-display)' }}>{current.title}</h3>
        <p className="text-[13px] text-muted-foreground leading-relaxed mb-4">{current.description}</p>

        <div className="flex items-center justify-between">
          <button onClick={finish} className="text-xs text-muted-foreground hover:text-foreground">
            Skip tour
          </button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="ghost" size="sm" onClick={prev} className="h-8 text-xs">
                <ChevronLeft className="h-3 w-3 mr-1" /> Back
              </Button>
            )}
            <Button size="sm" onClick={next} className="h-8 text-xs">
              {step === TOUR_STEPS.length - 1 ? 'Finish' : 'Next'}
              {step < TOUR_STEPS.length - 1 && <ChevronRight className="h-3 w-3 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export function useOnboarding() {
  const [showTour, setShowTour] = useState(false);
  // Phase 3B Batch 2 — M-CRIT-1: read backend-persisted dismissal flag from
  // the auth context. This survives full browser reloads on broken-localStorage
  // browsers.
  const { user } = useAuth();
  const backendDismissed = !!user?.tour_dismissed;

  useEffect(() => {
    // Layered short-circuits, cheapest first:
    //   1. Backend flag (survives full reloads on any browser)
    //   2. In-session flag (survives SPA route changes)
    //   3. localStorage (survives reloads when storage works)
    if (backendDismissed) return;
    if (TOUR_DISMISSED_THIS_SESSION) return;
    const done = _safeReadDone();
    if (!done) {
      const t = setTimeout(() => {
        // Re-check the session + backend flags at fire-time in case dismissal
        // happened during the 1.5s warmup window.
        if (TOUR_DISMISSED_THIS_SESSION) return;
        setShowTour(true);
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [backendDismissed]);

  const triggerTour = () => {
    TOUR_DISMISSED_THIS_SESSION = false;
    _safeClearDone();
    setShowTour(true);
  };

  return { showTour, setShowTour, triggerTour };
}
